import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { Grid, Button } from "semantic-ui-react";

// Utils
import { map, get, reduce } from "lodash";
import { Chart } from "react-google-charts";

// Redux
import genericAction from "@Actions/generic";
import { connect } from "react-redux";

// Components
import Screen from "@Components/Screen";
import NewTable from "@Components/NewTable";
import IkTable from "@Components/IkTable";
import PrintRender from "@Components/PrintRender";

// utils
import { getTimeLabel } from "@Utils/functionality";

// Styles
import styles from "./styles.module.css";

const baseChartOptions = {
	backgroundColor: "transparent",
	chartArea: { left: 10, top: 10, width: "95%", height: "80%" },
};

const Dashboard = ({ companyName, userName, dispatch, company, user }) => {
	const [printAction, setPrintAction] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const printRef = useRef();

	// table Handler
	const navigate = useNavigate();
	const handleButtom = (name, item) => {
		if (name === "Watch") {
			navigate("/monitor", { state: { state: item.state } });
		}
	};

	const refreshData = async () => {
		setIsLoading(true);
		try {
			const dashboardResponse = await dispatch(genericAction({ method: "get", route: "/dashboard" }));

			if (dashboardResponse) {
				// Platforms
				const platforms = get(dashboardResponse, "plataformas", []);
				const platformDataMapped = map(platforms, (p) => {
					const abiertas = get(p, "abiertas", {});
					return {
						platform: p.rowName,
						total: get(abiertas, "total", 0),
						nuevo: get(abiertas, "Nuevo", 0) || get(abiertas, "nuevo", 0),
						atender: get(abiertas, "Atender", 0) || get(abiertas, "atender", 0),
						cliente: get(abiertas, "Cliente", 0) || get(abiertas, "cliente", 0),
					};
				});
				setPlatformData(platformDataMapped);
				setPlatformChart([
					["Plataforma", "Total"],
					...map(platformDataMapped, (p) => {
						return [p.platform, p.total];
					}),
				]);

				// Channels
				const channels = get(dashboardResponse, "canales", []);
				const channelDataMapped = map(channels, (c) => {
					const abiertas = get(c, "abiertas", {});
					return {
						platform: get(c, "platformName", ""),
						channel: c.rowName,
						total: get(abiertas, "total", 0),
						nuevo: get(abiertas, "Nuevo", 0) || get(abiertas, "nuevo", 0),
						atender: get(abiertas, "Atender", 0) || get(abiertas, "atender", 0),
						cliente: get(abiertas, "Cliente", 0) || get(abiertas, "cliente", 0),
					};
				});
				setChannelData(channelDataMapped);
				setChannelChart([
					["Canal", "Total"],
					...map(channelDataMapped, (c) => {
						return [c.channel, c.total];
					}),
				]);

				// Conversations (States)
				const conversationsRaw = get(dashboardResponse, "conversaciones", []);
				const conversationsMapped = map(conversationsRaw, (c) => {
					const abiertas = get(c, "abiertas", {});
					return {
						state: c.rowName,
						total: get(abiertas, "total", 0),
						atiempo: get(abiertas, "atiempo", 0) || get(abiertas, "A Tiempo", 0),
						atraso: get(abiertas, "atraso", 0) || get(abiertas, "En Atraso", 0),
						vencido: get(abiertas, "vencido", 0) || get(abiertas, "Vencido", 0),
					};
				});
				setConversations(conversationsMapped);
				setConversationsChart([
					["Estado", "Total"],
					...map(conversationsMapped, (c) => {
						return [c.state, c.total];
					}),
				]);

				// Users
				const usersRaw = get(dashboardResponse, "usuarios", []);
				const usersMapped = map(usersRaw, (u) => {
					const abiertas = get(u, "abiertas", {});
					const cerradas = get(u, "cerradas", {});
					return {
						userName: u.rowName,
						chats: {
							total: get(abiertas, "total", 0),
							atiempo: get(abiertas, "atiempo", 0) || get(abiertas, "A Tiempo", 0),
							atraso: get(abiertas, "atraso", 0) || get(abiertas, "En Atraso", 0),
							vencido: get(abiertas, "vencido", 0) || get(abiertas, "Vencido", 0),
						},
						chatsh: {
							total: get(cerradas, "total", 0),
							atiempo: get(cerradas, "atiempo", 0) || get(cerradas, "A Tiempo", 0),
							atraso: get(cerradas, "atraso", 0) || get(cerradas, "En Atraso", 0),
							vencido: get(cerradas, "vencido", 0) || get(cerradas, "Vencido", 0),
						},
						total: get(abiertas, "total", 0) + get(cerradas, "total", 0),
					};
				});
				setConversationsByUsers(usersMapped);
				setUserChart([
					["Usuario", "Total"],
					...map(usersMapped, (u) => {
						return [u.userName, u.total];
					}),
				]);

				// SumData
				const sumDataCalc = {
					"chats.total": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chats.total;
						},
						0
					),
					"chats.atiempo": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chats.atiempo;
						},
						0
					),
					"chats.atraso": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chats.atraso;
						},
						0
					),
					"chats.vencido": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chats.vencido;
						},
						0
					),
					"chatsh.total": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chatsh.total;
						},
						0
					),
					"chatsh.atiempo": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chatsh.atiempo;
						},
						0
					),
					"chatsh.atraso": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chatsh.atraso;
						},
						0
					),
					"chatsh.vencido": reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.chatsh.vencido;
						},
						0
					),
					total: reduce(
						usersMapped,
						(sum, u) => {
							return sum + u.total;
						},
						0
					),
				};
				setSumData(sumDataCalc);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const [conversations, setConversations] = useState([]);
	const [conversationsByUsers, setConversationsByUsers] = useState([]);
	const [conversationsChart, setConversationsChart] = useState([]);
	const [userChart, setUserChart] = useState([]);
	const [platformChart, setPlatformChart] = useState([]);
	const [platformData, setPlatformData] = useState([]);
	const [channelChart, setChannelChart] = useState([]);
	const [channelData, setChannelData] = useState([]);
	const [sumData, setSumData] = useState({});
	// const [userMessagesData, setUserMessagesData] = useState([]);

	useEffect(() => {
		refreshData();
	}, []);

	return (
		<Screen hasHeader hasSidebar headerTitle="Dashboard">
			<div className={styles.container}>
				<div className={styles.containerDashboard}>
					<div className={styles.containerDashboardHeader}>
						<span className={styles.headerText}>{getTimeLabel(new Date(), "dddd, D [de] MMMM [de] YYYY")}</span>
						<div>
							<Button
								className={styles.printButton}
								icon="refresh"
								onClick={refreshData}
								style={{ marginRight: "10px" }}
								loading={isLoading}
							/>
							<Button
								className={`${styles.printButton} ${styles.hideOnMobile}`}
								icon="print"
								onClick={() => {
									return setPrintAction(true);
								}}
								disabled={isLoading}
							/>
						</div>
					</div>

					<div className={styles.containerDashboard} ref={printRef}>
						<Grid columns={2} stackable>
							<Grid.Column width={16} className={styles.column}>
								<Grid.Row>
									<div className={styles.containerTable}>
										<div className={`${styles.containerTableHeader} ${styles.hideOnMobile}`}>
											<span className={styles.headerText}>Plataformas / Canales</span>
										</div>
										<div className={styles.containerLeftPri}>
											<div className={styles.containerTableContent}>
												<div className={styles.mobileOnlyHeader}>
													<span className={styles.mobileHeaderText}>Plataformas</span>
												</div>
												<div className={styles.containerLeft}>
													<NewTable
														data={platformData}
														mobileCardView
														cardLine1={[
															{ label: "", key: "platform" },
															{ label: "Total", key: "total" },
														]}
														cardLine2={[
															{ label: "Nuevo", key: "nuevo" },
															{ label: "Atender", key: "atender" },
															{ label: "Cliente", key: "cliente" },
														]}
														columns={[
															{ label: "Plataforma", key: "platform", format: "blankZero", dataPosition: "left", width: 2 },
															{ label: "Total", key: "total", format: "blankZero", dataPosition: "center", width: 1, sum: true },
															{ label: "Nuevo", key: "nuevo", format: "blankZero", dataPosition: "center", width: 1 },
															{ label: "Atender", key: "atender", format: "blankZero", dataPosition: "center", width: 1 },
															{ label: "Cliente", key: "cliente", format: "blankZero", dataPosition: "center", width: 1 },
														]}
														defaultOrder={{ key: "total", dir: "desc" }}
														pageRows={10}
														searchable={false}
														haveFooter={false}
														searchPosition="left"
														handlerTable={handleButtom}
														printable={false}
														addButton={false}
														printHeader={{ company: companyName, title: "Plataformas / Canales", user: userName }}
														onChangeReset={3}
														columnSum
													/>
												</div>
												<div className={styles.containerRight}>
													<div className={styles.chartWrapper}>
														<Chart
															chartType="PieChart"
															height="200px"
															width="100%"
															loader={<div className={styles.loading}>Loading Chart</div>}
															data={platformChart}
															options={{
																...baseChartOptions,
																bar: { groupWidth: "98%" },
																legend: { position: "right", alignment: "center", textStyle: { fontSize: 11, color: "#333" } },
															}}
														/>
													</div>
												</div>
											</div>
											<div className={styles.containerTableContent}>
												<div className={styles.mobileOnlyHeader}>
													<span className={styles.mobileHeaderText}>Canales</span>
												</div>
												<div className={styles.containerLeft}>
													<NewTable
														data={channelData}
														mobileCardView
														cardLine1={[
															{ label: "Plataforma", key: "platform" },
															{ label: "Tel/Cuenta", key: "channel" },
															{ label: "Total", key: "total" },
														]}
														cardLine2={[
															{ label: "Nuevo", key: "nuevo" },
															{ label: "Atender", key: "atender" },
															{ label: "Cliente", key: "cliente" },
														]}
														columns={[
															{ label: "Plataforma", key: "platform", format: "string", dataPosition: "left", width: 2 },
															{ label: "Tel/Cuenta", key: "channel", format: "blankZero", dataPosition: "left", width: 2 },
															{ label: "Total", key: "total", format: "blankZero", dataPosition: "center", width: 1, sum: true },
															{ label: "Nuevo", key: "nuevo", format: "blankZero", dataPosition: "center", width: 1 },
															{ label: "Atender", key: "atender", format: "blankZero", dataPosition: "center", width: 1 },
															{ label: "Cliente", key: "cliente", format: "blankZero", dataPosition: "center", width: 1 },
														]}
														defaultOrder={{ key: "total", dir: "desc" }}
														pageRows={10}
														searchable={false}
														haveFooter={false}
														searchPosition="left"
														handlerTable={handleButtom}
														printable={false}
														addButton={false}
														printHeader={{ company: companyName, title: "Canales", user: userName }}
														onChangeReset={3}
														columnSum
													/>
												</div>
												<div className={styles.containerRight}>
													<div className={styles.chartWrapper}>
														<Chart
															chartType="PieChart"
															height="200px"
															width="100%"
															loader={<div className={styles.loading}>Loading Chart</div>}
															data={channelChart}
															options={{
																...baseChartOptions,
																bar: { groupWidth: "98%" },
																legend: { position: "right", alignment: "center", textStyle: { fontSize: 11, color: "#333" } },
															}}
														/>
													</div>
												</div>
											</div>
										</div>
									</div>
								</Grid.Row>
							</Grid.Column>

							<Grid.Row>
								<Grid.Column width={16} className={styles.column}>
									<div className={styles.containerTable}>
										<div className={`${styles.containerTableHeader} ${styles.hideOnMobile}`}>
											<span className={styles.headerText}>Conversaciones / Usuarios</span>
										</div>
										<div className={styles.containerLeftPri}>
											<div className={styles.containerTableContent}>
												<div className={styles.mobileOnlyHeader}>
													<span className={styles.mobileHeaderText}>Conversaciones</span>
												</div>
												<div className={`${styles.containerLeft} ${styles.fullWidthContent}`}>
													<NewTable
														data={conversations}
														mobileCardView
														cardLine1={[
															{ label: "", key: "state" },
															{ label: "Total", key: "total" },
														]}
														cardLine2={[
															{ label: "A Tiempo", key: "atiempo" },
															{ label: "En Atraso", key: "atraso" },
															{ label: "Vencido", key: "vencido" },
														]}
														columns={[
															{
																label: "Estado",
																key: "state",
																format: "blankZero",
																dataPosition: "center",
																width: 1,
															},
															{
																label: "Total",
																key: "total",
																format: "blankZero",
																width: 1,
																dataPosition: "center",
																sum: true,
															},
															{
																label: "A Tiempo",
																key: "atiempo",
																format: "blankZero",
																width: 1,
																dataPosition: "center",
															},
															{
																label: "En Atraso",
																key: "atraso",
																format: "blankZero",
																width: 1,
																dataPosition: "center",
															},
															{
																label: "Vencido",
																headerPosition: "left",
																key: "vencido",
																format: "blankZero",
																width: 11,
															},
														]}
														defaultOrder={{ key: "userId", dir: "desc" }}
														pageRows={10}
														searchable={false}
														haveFooter={false}
														searchPosition="left"
														handlerTable={handleButtom}
														printable={false}
														addButton={false}
														printHeader={{ company: companyName, title: "Usuarios", user: userName }}
														onChangeReset={3}
														columnSum
													/>
												</div>
												<div className={`${styles.containerRight} ${styles.fullWidthContent}`}>
													<div className={styles.chartWrapper}>
														<Chart
															chartType="PieChart"
															height="200px"
															width="100%"
															loader={<div className={styles.loading}>Loading Chart</div>}
															data={conversationsChart}
															options={{
																...baseChartOptions,
																bar: { groupWidth: "98%" },
																legend: { position: "right", alignment: "center", textStyle: { fontSize: 11, color: "#333" } },
															}}
														/>
													</div>
												</div>
											</div>
											<div className={styles.containerTableContent}>
												<div className={styles.mobileOnlyHeader}>
													<span className={styles.mobileHeaderText}>Usuarios</span>
												</div>
												<div className={`${styles.containerLeftl} ${styles.fullWidthContent}`}>
													<IkTable
														data={conversationsByUsers}
														mobileCardView
														cardLine1={[
															{
																label: "",
																key: "userName",
																render: (rowData) => {
																	return {
																		label: "",
																		value: <div className={styles.userNameHeader}>{rowData.userName}</div>,
																	};
																},
															},
														]}
														cardLine2={[
															{
																label: "",
																key: "chats",
																render: (rowData) => {
																	return {
																		label: "",
																		value: (
																			<div style={{ width: "100%" }}>
																				<div className={styles.userStatsContainer}>
																					<div className={`${styles.userStatsSection} ${styles.userStatsSectionOpen}`}>
																						<div className={`${styles.userStatsLabel} ${styles.userStatsLabelOpen}`}>Abiertas:</div>
																						<div className={styles.userStatsMetrics}>
																							<span className={styles.userStatsMetric}>Total: {get(rowData, "chats.total", 0)}</span>
																							<span className={styles.userStatsMetric}>A Tiempo: {get(rowData, "chats.atiempo", 0)}</span>
																							<span className={styles.userStatsMetric}>En Atraso: {get(rowData, "chats.atraso", 0)}</span>
																							<span className={styles.userStatsMetric}>Vencido: {get(rowData, "chats.vencido", 0)}</span>
																						</div>
																					</div>
																					<div className={`${styles.userStatsSection} ${styles.userStatsSectionClosed}`}>
																						<div className={`${styles.userStatsLabel} ${styles.userStatsLabelClosed}`}>Cerradas:</div>
																						<div className={styles.userStatsMetrics}>
																							<span className={styles.userStatsMetric}>Total: {get(rowData, "chatsh.total", 0)}</span>
																							<span className={styles.userStatsMetric}>A Tiempo: {get(rowData, "chatsh.atiempo", 0)}</span>
																							<span className={styles.userStatsMetric}>En Atraso: {get(rowData, "chatsh.atraso", 0)}</span>
																							<span className={styles.userStatsMetric}>Vencido: {get(rowData, "chatsh.vencido", 0)}</span>
																						</div>
																					</div>
																				</div>
																			</div>
																		),
																	};
																},
															},
														]}
														columns={[
															{
																label: "Usuarios",
																key: "userName",
																format: "string",
																width: 11,
															},
															{
																label: "Abierta",
																multipleColumns: [
																	{
																		label: "Total",
																		key: "chats.total",
																		format: "blankZero",
																		width: 1,
																		dataPosition: "center",
																		sum: true,
																	},
																	{
																		label: "A Tiempo",
																		key: "chats.atiempo",
																		format: "blankZero",
																		dataPosition: "center",
																	},
																	{
																		label: "En Atraso",
																		key: "chats.atraso",
																		format: "blankZero",
																		dataPosition: "center",
																	},
																	{
																		label: "Vencido",
																		key: "chats.vencido",
																		format: "blankZero",
																		dataPosition: "center",
																	},
																],
															},
															{
																label: "Cerradas",
																multipleColumns: [
																	{
																		label: "Total",
																		key: "chatsh.total",
																		format: "blankZero",
																		width: 1,
																		dataPosition: "center",
																		sum: true,
																	},
																	{
																		label: "A Tiempo",
																		key: "chatsh.atiempo",
																		format: "blankZero",
																		dataPosition: "center",
																	},
																	{
																		label: "En Atraso",
																		key: "chatsh.atraso",
																		format: "blankZero",
																		dataPosition: "center",
																	},
																	{
																		label: "Vencido",
																		key: "chatsh.vencido",
																		format: "blankZero",
																		dataPosition: "center",
																	},
																],
															},
															{ label: "Total", key: "total", format: "string", width: 3 },
														]}
														defaultOrder={{ key: "userId", dir: "desc" }}
														pageRows={10}
														searcher={{
															searchable: false,
															searchPosition: "flex-start",
															searchDisabled: false,
															filterButton: true,
														}}
														searchPosition="left"
														handlerTable={handleButtom}
														printable={false}
														addButton={{}}
														visiblePagination={false}
														sumData={sumData}
														topPlaceClassName={styles.customTopPlace}
													/>
												</div>
												<div className={`${styles.containerRight} ${styles.fullWidthContent}`}>
													<div className={styles.chartWrapper}>
														<Chart
															chartType="PieChart"
															height="200px"
															width="100%"
															loader={<div className={styles.loading}>Loading Chart</div>}
															data={userChart}
															options={{
																...baseChartOptions,
																bar: { groupWidth: "98%" },
																legend: { position: "right", alignment: "center", textStyle: { fontSize: 11, color: "#333" } },
															}}
														/>
													</div>
												</div>
											</div>
										</div>
									</div>
								</Grid.Column>
							</Grid.Row>
						</Grid>
					</div>
				</div>
			</div>

			<PrintRender
				hiddenButon
				printAction={printAction}
				printActionRetry={() => {
					setPrintAction(false);
				}}
				printHeader={{
					company: company.name,
					user: user.name,
					title: "Dashboard",
				}}
				renderData={printRef}
				hideClientInfo
			/>
		</Screen>
	);
};
function mapStateToProps({ user, company }) {
	return {
		company,
		user,
		userNamess: user.fullName,
	};
}

Dashboard.propTypes = {
	userName: PropTypes.string,
	companyName: PropTypes.string,
	dispatch: PropTypes.func,
	company: PropTypes.object,
	user: PropTypes.object,
};

export default connect(mapStateToProps)(Dashboard);
